import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { Ripple } from "./ripple"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative overflow-hidden active:scale-95 touch-manipulation",
  {
    variants: {
      variant: {
        default: "bg-primary text-white hover:bg-primary/90",
        destructive:
          "bg-red-500 text-white hover:bg-red-600",
        outline:
          "border border-input bg-background text-gray-900 hover:bg-accent hover:text-gray-900",
        secondary:
          "bg-secondary text-gray-900 hover:bg-secondary/80",
        success:
          "bg-emerald-600 text-white hover:bg-emerald-700",
        warning:
          "bg-amber-500 text-white hover:bg-amber-600",
        ghost: "text-gray-700 hover:bg-accent hover:text-gray-900",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"

    // Only add ripple effect on mobile
    const isMobile = typeof window !== 'undefined' &&
      (window.navigator.userAgent.includes('Mobile') ||
        (window as any).Capacitor);

    if (isMobile && !asChild) {
      return (
        <Ripple disabled={props.disabled}>
          <Comp
            className={cn(buttonVariants({ variant, size }), className)}
            ref={ref}
            {...props}
          />
        </Ripple>
      )
    }

    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
